import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { BookOpen, ArrowRight, Pencil } from "lucide-react";
import { LowReadabilityCard } from "./LowReadabilityCard";
import { ReadabilityMeter } from "./ReadabilityMeter";
import type {
	PageIssue,
	LowReadabilityDetails,
	LowReadabilityFix,
	ProposedFix,
	SentenceDiff,
} from "@/types/technicalAudit";

interface LowReadabilityDiffCardProps {
	issue: PageIssue;
	/**
	 * Called whenever the specialist edits the suggestion, with the value the
	 * backend applies. In sentence mode this is `{ sentence_diffs }` (the backend
	 * reassembles approved edits against the live body); in legacy block mode it
	 * is `{ rewritten_body }`. The drawer stores it as `editedValue` and sends it
	 * as `approvedValue`.
	 */
	onEdit?: (value: ProposedFix) => void;
}

// The suggestion lives in proposed_fix (preferred) / approved_value / proposed_value,
// as either a parsed object or a JSON string. Read it leniently so the card
// never crashes (mirrors OrphanPageCard).
function readReadabilityFix(issue: PageIssue): LowReadabilityFix | null {
	const raw =
		(issue.proposed_fix as unknown) ??
		(issue.approved_value as unknown) ??
		(issue.proposed_value as unknown);
	if (!raw) return null;
	if (typeof raw === "object" && !Array.isArray(raw)) return raw as LowReadabilityFix;
	if (typeof raw === "string") {
		const trimmed = raw.trim();
		if (!trimmed) return null;
		try {
			const parsed = JSON.parse(trimmed);
			return parsed && typeof parsed === "object" && !Array.isArray(parsed)
				? (parsed as LowReadabilityFix)
				: null;
		} catch {
			return null;
		}
	}
	return null;
}

export function LowReadabilityDiffCard({ issue, onEdit }: LowReadabilityDiffCardProps) {
	const details = issue.issue_details as LowReadabilityDetails;
	const fix = readReadabilityFix(issue);

	// Sentence-level review: plain text + highlighted suggestions. The operator
	// reviews the SENTENCES that changed, never raw markup — a rewritten page
	// dumped into a textarea is not something anyone can meaningfully approve.
	const sentenceDiffs: SentenceDiff[] = Array.isArray(fix?.sentence_diffs)
		? (fix!.sentence_diffs as SentenceDiff[])
		: [];
	const sentenceMode = sentenceDiffs.length > 0;
	// A saved payload with only `rewritten_body` predates sentence review.
	// It must be regenerated, not edited as HTML.
	const staleLegacySentencePayload = !sentenceMode && Boolean(fix?.rewritten_body);

	const hasSuggestion = Boolean(fix?.rewritten_body) || sentenceDiffs.length > 0;
	const draftKey = `${issue.id}:${JSON.stringify(issue.proposed_fix ?? issue.approved_value ?? issue.proposed_value ?? "")}`;

	// Sentence-mode draft (per-sentence accept + editable suggestion).
	type SItem = { accepted: boolean; proposed: string };
	const initialItems: SItem[] = sentenceDiffs.map((d) => ({
		accepted: true,
		proposed: d.proposed,
	}));
	const [sentDraft, setSentDraft] = useState<{ key: string; items: SItem[] } | null>(null);
	const items = sentDraft?.key === draftKey ? sentDraft.items : initialItems;
	const acceptedCount = items.filter((it) => it.accepted).length;

	const updateItem = (i: number, patch: Partial<SItem>) => {
		const base = sentDraft?.key === draftKey ? sentDraft.items : initialItems;
		setSentDraft({
			key: draftKey,
			items: base.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
		});
	};

	// Stable serialization so the push-up effect only fires on real changes.
	const acceptedKey = JSON.stringify(
		sentenceDiffs.map((d, i) =>
			items[i]?.accepted ? [d.block_index, d.sentence_index, items[i].proposed] : null,
		),
	);

	// Push the right payload up so Approve/Apply send exactly what's reviewed.
	useEffect(() => {
		if (!hasSuggestion) return;
		if (!sentenceMode) {
			// Stale full-body payload: send nothing, so Approve/Apply cannot ship
			// a page body no one reviewed.
			onEdit?.({} as ProposedFix);
			return;
		}
		const accepted = sentenceDiffs
			.map((d, i) => (items[i]?.accepted ? { ...d, proposed: items[i].proposed } : null))
			.filter((d): d is SentenceDiff => d !== null);
		onEdit?.({ sentence_diffs: accepted } as ProposedFix);
		// acceptedKey captures the meaningful sentence-draft change; items/sentenceDiffs
		// are read inside but would refire every render as fresh refs.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sentenceMode, acceptedKey, hasSuggestion, onEdit]);

	// No suggestion yet — show the read-only score card plus a hint to generate.
	if (!hasSuggestion) {
		return (
			<div className="space-y-4">
				<LowReadabilityCard issue={issue} />
				<Card className="border-border shadow-sm">
					<CardContent className="py-4 text-sm text-muted-foreground">
						Click <span className="font-medium">Generate fix</span> above to draft an
						AI rewrite of the page's hardest-to-read sentences. You can review and edit
						each suggestion before applying. The page's links, images and headings are
						preserved.
					</CardContent>
				</Card>
			</div>
		);
	}

	const scoreBefore =
		typeof fix?.readability_score_before === "number"
			? fix.readability_score_before
			: details.readability_score;
	const scoreAfter = fix?.readability_score_after;
	const gradeBefore = fix?.grade_before || details.grade_level;
	const gradeAfter = fix?.grade_after;
	const blocksRewritten = fix?.blocks_rewritten;
	const blocksTotal = fix?.blocks_total;

	return (
		<div className="space-y-4">
			<Card className="border-teal/30 shadow-sm bg-teal-light/40">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-teal-dark flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-teal-light flex items-center justify-center">
							<BookOpen className="w-4 h-4 text-teal-dark" />
						</div>
						AI Readability Rewrite
						<Badge
							variant="outline"
							className="ml-auto bg-teal-light text-teal-dark border-teal/30"
						>
							{sentenceMode ? "sentence-level" : staleLegacySentencePayload ? "regenerate" : "editable"}
						</Badge>
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-4">
					{/* Before -> After readability badge */}
					<div className="rounded-lg border border-teal/20 bg-card p-3">
						<div className="text-[11px] font-medium uppercase tracking-wide text-teal-dark mb-2">
							Readability (Flesch Reading Ease — higher is easier)
						</div>
						<div className="flex items-center justify-center gap-3">
							<div className="text-center">
								<div className="text-xl font-bold text-coral-dark">{scoreBefore}</div>
								<div className="text-xs text-muted-foreground">{gradeBefore || "—"}</div>
							</div>
							<ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
							<div className="text-center">
								<div className="text-xl font-bold text-teal-dark">
									{typeof scoreAfter === "number" ? scoreAfter : "—"}
								</div>
								<div className="text-xs text-muted-foreground">{gradeAfter || "—"}</div>
							</div>
						</div>
						{sentenceMode ? (
							<div className="mt-2 text-center text-xs text-muted-foreground">
								{acceptedCount} sentence{acceptedCount === 1 ? "" : "s"} selected to simplify
							</div>
						) : staleLegacySentencePayload ? (
							<div className="mt-2 text-center text-xs text-amber-700 dark:text-amber-400">
								Saved legacy full-body proposal; regenerate for sentence-level review
							</div>
						) : (
							typeof blocksRewritten === "number" &&
							typeof blocksTotal === "number" && (
								<div className="mt-2 text-center text-xs text-muted-foreground">
									{blocksRewritten} of {blocksTotal} paragraph
									{blocksTotal === 1 ? "" : "s"} simplified
								</div>
							)
						)}
					</div>

					{sentenceMode ? (
						/* Sentence-level review: plain text, suggestion highlighted, no raw HTML */
						<div className="space-y-2">
							<div className="flex items-center justify-between">
								<label className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-teal-dark">
									<Pencil className="w-3 h-3" /> Suggested sentence edits
								</label>
								<span className="text-xs text-muted-foreground">
									{acceptedCount} of {sentenceDiffs.length} selected
								</span>
							</div>
							{sentenceDiffs.map((d, i) => {
								const accepted = items[i]?.accepted ?? true;
								return (
									<div
										key={`${d.block_index}-${d.sentence_index}`}
										className="rounded-lg border border-teal/20 bg-card p-3"
									>
										<div className="flex items-start gap-2">
											<input
												type="checkbox"
												checked={accepted}
												onChange={(e) => updateItem(i, { accepted: e.target.checked })}
												className="mt-1 h-4 w-4 accent-teal-dark shrink-0"
												aria-label="Include this sentence edit"
											/>
											<div className="min-w-0 flex-1 space-y-2">
												<div>
													<div className="flex flex-wrap items-center gap-2">
														<div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
															Original
														</div>
														{typeof d.grade_before === "number" && (
															<Badge
																variant="outline"
																className="border-coral/30 bg-coral-light text-[10px] text-coral-dark"
															>
																Reading grade {d.grade_before}
																{typeof d.grade_after === "number" &&
																	` → ${d.grade_after}`}
															</Badge>
														)}
													</div>
													<p className="text-sm text-muted-foreground">{d.original}</p>
													{/* Why this sentence was flagged — the numbers behind
													    the grade, so the edit is reviewable on evidence. */}
													{Array.isArray(d.reasons) && d.reasons.length > 0 && (
														<ul className="mt-1.5 space-y-0.5">
															{d.reasons.map((r) => (
																<li
																	key={r.code}
																	className="text-xs text-muted-foreground"
																>
																	<span className="font-medium text-foreground">
																		{r.label}:
																	</span>{" "}
																	{r.detail}
																</li>
															))}
														</ul>
													)}
												</div>
												<ReadabilityMeter text={d.original} caption="Original" />
												<div>
													<div className="text-[11px] font-medium uppercase tracking-wide text-teal-dark">
														Suggested
													</div>
													<Textarea
														value={items[i]?.proposed ?? d.proposed}
														onChange={(e) => updateItem(i, { proposed: e.target.value })}
														disabled={!accepted}
														className="mt-0.5 min-h-[56px] text-sm bg-teal-light/40 border-teal/30 disabled:opacity-50"
													/>
													{/* Moves as they type, so the effect of each word is
													    visible without waiting for the backend to re-score. */}
													<ReadabilityMeter
														text={items[i]?.proposed ?? d.proposed}
														compareTo={d.original}
														caption="Your edit"
														className="mt-1.5"
													/>
												</div>
											</div>
										</div>
									</div>
								);
							})}
							<p className="text-xs text-muted-foreground">
								Only the hardest-to-read sentences are shown. Uncheck any you want to keep
								as-is, or edit a suggestion. Headings, links, images and numbers are never
								changed; the backend re-verifies readability on apply.
							</p>
						</div>
					) : staleLegacySentencePayload ? (
						<Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 shadow-none">
							<CardContent className="space-y-2 p-3">
								<div className="text-[11px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-400">
									Stale full-body proposal
								</div>
								<p className="text-sm text-amber-950">
									This saved suggestion was generated before sentence-level review and
									contains the full page body. Use Regenerate fix above to create
									sentence-level edits instead of editing raw Divi, Avada, or HTML here.
								</p>
								<p className="text-xs text-amber-800 dark:text-amber-300">
									Regenerate replaces the saved proposal in Technical Audit, but it does
									not apply anything to WordPress.
								</p>
							</CardContent>
						</Card>
					) : null}

					{fix?.reasoning && (
						<div className="rounded-md bg-card border border-teal/20 p-3">
							<p className="text-xs font-medium text-teal-dark">AI Reasoning:</p>
							<p className="mt-1 text-xs text-foreground">{fix.reasoning}</p>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
